import { cn } from '@/utils/cn';
import { ErrorMessage, Field, FieldProps, useFormikContext } from 'formik';
import { forwardRef } from 'react';

interface TextAreaProps {
  label?: string;
  name?: string;
  error?: boolean | string;
  placeholder?: string;
  containerStyle?: string;
  labelStyle?: string;
  textareaStyle?: string;
  isRequired?: boolean;
  rows?: number;
  value?: string;
  onChange?: (value: string) => void;
  disabled?: boolean;
  useFormik?: boolean;
  maxLength?: number;
}

// Component that uses Formik
const FormikTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      name,
      error,
      containerStyle,
      placeholder,
      labelStyle,
      textareaStyle,
      isRequired = false,
      rows = 4,
      disabled,
      maxLength,
    },
    ref,
  ) => {
    // Always call useFormikContext at the top level
    useFormikContext(); // Required to be in Formik context
    return (
      <div className={cn(`relative flex flex-col space-y-2`, containerStyle)}>
        {label && (
          <label
            className={cn(`text-xs font-medium text-gray-600`, labelStyle)}
          >
            {label}
            {isRequired && <span className="ml-1 text-danger">*</span>}
          </label>
        )}

        {isRequired && !label && (
          <span className="absolute left-0 top-1 text-xs text-danger">*</span>
        )}

        <Field name={name}>
          {({ field }: FieldProps) => (
            <>
              <textarea
                {...field}
                ref={ref}
                rows={rows}
                placeholder={placeholder}
                disabled={disabled}
                maxLength={maxLength}
                className={cn(
                  `min-h-[120px] w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-gray-800 shadow-sm transition-colors focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200`,
                  error
                    ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                    : '',
                  textareaStyle,
                )}
              />
              {maxLength && (
                <div className="flex justify-end">
                  <span className="text-xs text-gray-500">
                    {(field.value || '').length}/{maxLength}
                  </span>
                </div>
              )}
            </>
          )}
        </Field>

        {typeof error === 'string' && (
          <span className="text-sm text-danger">{error}</span>
        )}
        <ErrorMessage
          name={name as string}
          component="span"
          className="text-sm text-danger"
        />
      </div>
    );
  },
);

// Component that doesn't use Formik
const StandardTextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      label,
      name,
      error,
      containerStyle,
      placeholder,
      labelStyle,
      textareaStyle,
      isRequired = false,
      rows = 4,
      value,
      onChange,
      disabled,
      maxLength,
    },
    ref,
  ) => {
    return (
      <div className={cn(`relative flex flex-col space-y-2`, containerStyle)}>
        {label && (
          <label
            className={cn(`text-xs font-medium text-gray-600`, labelStyle)}
          >
            {label}
            {isRequired && <span className="ml-1 text-danger">*</span>}
          </label>
        )}

        {isRequired && !label && (
          <span className="absolute left-0 top-1 text-xs text-danger">*</span>
        )}

        <textarea
          ref={ref}
          name={name}
          rows={rows}
          placeholder={placeholder}
          value={value}
          onChange={e => onChange?.(e.target.value)}
          disabled={disabled}
          maxLength={maxLength}
          className={cn(
            `min-h-[120px] w-full resize-none rounded-lg border border-gray-300 px-3 py-2 text-gray-800 shadow-sm transition-colors focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200`,
            error
              ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
              : '',
            textareaStyle,
          )}
        />

        {maxLength && (
          <div className="flex justify-end">
            <span className="text-xs text-gray-500">
              {(value || '').length}/{maxLength}
            </span>
          </div>
        )}

        {typeof error === 'string' && (
          <span className="text-sm text-danger">{error}</span>
        )}
      </div>
    );
  },
);

// Main TextArea component that decides which implementation to use
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (props, ref) => {
    const { useFormik = true } = props;
    // If useFormik is explicitly set to false, use StandardTextArea
    if (useFormik === false) {
      return <StandardTextArea {...props} ref={ref} />;
    }
    // Otherwise, try to use FormikTextArea and fall back to StandardTextArea if it fails
    try {
      return <FormikTextArea {...props} ref={ref} />;
    } catch {
      // If FormikTextArea fails (no Formik context), fall back to StandardTextArea
      return <StandardTextArea {...props} ref={ref} />;
    }
  },
);
